import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const SarPass = () => {
  const tourData = {
    name: "Sar Pass Trek",
    duration: "04 Nights 05 Days",
    price: "₹ 4,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1754935181/fkrfan4lfb0dcod97anh.jpg",
    altitude: "13,800 ft",
    difficulty: "Moderate",
    region: "Himachal Pradesh",
    highlights: ["Mountain pass", "Alpine lakes", "Panoramic views"],
    bestTime: "May to October",
    description: "The Sar Pass Trek is an exhilarating adventure through the pristine landscapes of Himachal Pradesh. This moderate trek takes you through dense forests, alpine meadows, and snow-covered passes, offering breathtaking views of the Pir Panjal range."
  };

  return (
    <>
      <Helmet>
        <title>Sar Pass Trek - NomadTrailz</title>
        <meta name="description" content="Experience the thrilling Sar Pass Trek in Himachal Pradesh. Alpine lakes, mountain passes, and panoramic Himalayan views await you." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img src={tourData.image} alt={tourData.name} />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              An exhilarating journey through alpine lakes and mountain passes
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div className="info-card" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>This trek offers a perfect blend of adventure and natural beauty, taking you through some of the most scenic landscapes in the Kullu Valley region.</p>
                </motion.div>

                <motion.div className="highlights-card" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
                  <h3>Highlights</h3>
                  <ul>
                    <li>Sar Pass at 13,800 ft</li>
                    <li>Alpine lakes and meadows</li>
                    <li>Panoramic mountain views</li>
                    <li>Dense pine forests</li>
                    <li>Traditional villages</li>
                    <li>Snow-covered landscapes</li>
                  </ul>
                </motion.div>

                <motion.div className="itinerary-card" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.4 }}>
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: From Kasol to Grahan Village</h4>
                    <ul>
                      <li>Your journey to the Sar Pass trek begins in the heart of Kasol and continues along the actual left bank of the Grahan Nallah.</li>
                      <li>The route initially follows an easy, well-traveled path used by locals, allowing you to cover a lot of ground in just a few hours.</li>
                      <li>As one leaves the Grahan Nallah behind, the path becomes stony and climbs upward into the Rhododendron forest, away from the river's right bank.</li>
                      <li>After that, an hour of strenuous climbing leads to Grahan settlement, which is the final inhabited village on this trek.</li>
                      <li>From this point onward, there is no cell network, so staying connected becomes impossible.</li>
                      <li>Therefore, it's essential to prepare for this lack of connectivity.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Grahan Village to Mung Thach</h4>
                    <ul>
                      <li>The second day involves trekking from Grahan Village to Mung Thach, passing through beautiful landscapes.</li>
                      <li>Experience the transition from inhabited areas to wilderness as you ascend higher.</li>
                      <li>The trail offers stunning views of the surrounding mountains and valleys.</li>
                      <li>You'll pass through dense forests and alpine meadows along the way.</li>
                      <li>Camp at Mung Thach for the night, enjoying the peaceful mountain environment.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Mung Thach to Nagaru</h4>
                    <ul>
                      <li>This day takes you to higher altitudes as you trek from Mung Thach to Nagaru.</li>
                      <li>The trail becomes more challenging with steep ascents and the landscape transforms into alpine meadows.</li>
                      <li>You'll encounter snow-covered patches as you gain elevation.</li>
                      <li>The views become more spectacular as you climb higher into the mountains.</li>
                      <li>Camp at Nagaru, experiencing the high-altitude environment.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: Nagaru to Sar Pass and Biskeri Thach</h4>
                    <ul>
                      <li>The most exciting day of the trek! You'll reach the Sar Pass summit at 13,800 feet.</li>
                      <li>Offering breathtaking panoramic views of the Himalayan ranges from the summit.</li>
                      <li>The highlight is the exhilarating slide from the Sar Pass summit to Biskeri Thach.</li>
                      <li>This provides an unforgettable adventure thrill as you glide down snow-covered slopes.</li>
                      <li>Camp at Biskeri Thach, celebrating your successful summit achievement.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 5: Biskeri Thach to Kasol</h4>
                    <ul>
                      <li>The final day involves descending back to Kasol, completing the circular trek.</li>
                      <li>You'll retrace your steps through the beautiful landscapes, ending your adventure where it began.</li>
                      <li>Enjoy the descent through the same scenic routes you climbed.</li>
                      <li>Arrive back in Kasol with memories of an incredible Himalayan adventure.</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="things-to-carry-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Things To Carry</h3>
                  <p className="carry-intro">All items listed are essential, unless marked as optional. Items are divided into five categories for your convenience.</p>
                  
                  <div className="carry-categories">
                    <div className="carry-category">
                      <h4><i className="fas fa-hiking"></i> Trekking Gear</h4>
                      <ul>
                        <li>Rucksack with rain cover</li>
                        <li>Day pack bag</li>
                        <li>Head torch with spare batteries</li>
                        <li>UV protection sunglasses</li>
                        <li>Water bottles: Two 1-liter bottles</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-shoe-prints"></i> Footwear</h4>
                      <ul>
                        <li>Non-skid, deep treaded, high-ankle trekking shoes: 1 pair</li>
                        <li>Lightweight slippers or sandals: 1 pair</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-tshirt"></i> Clothing</h4>
                      <ul>
                        <li>Quick-dry warm lower or track pants: 2</li>
                        <li>Full-sleeve T-shirts/sweatshirts: 1 for every 2 days of trekking</li>
                        <li>Thick woolen socks: 1 pair for every 2 days of trekking</li>
                        <li>Thermal body warmer (upper & lower): 1 set</li>
                        <li>Undergarments: 1 for each day of trekking</li>
                        <li>Warm jacket (closed at wrist & neck): 1</li>
                        <li>Full-sleeve sweater: 1</li>
                        <li>Rainwear (jacket & pants): 1 set</li>
                        <li>Waterproof, warm gloves: 1 pair</li>
                        <li>Woolen cap: 1</li>
                        <li>Sun-shielding hat: 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-soap"></i> Toiletries</h4>
                      <ul>
                        <li>Personal toiletries kit: Small towel, toilet paper, paper soap, bar soap, toothbrush, toothpaste, cold cream, etc.</li>
                        <li>Sunscreen lotion: Small pack</li>
                        <li>Lip balm: Small pack</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-utensils"></i> Utensils</h4>
                      <ul>
                        <li>Small, lightweight, leak-proof lunch box: 1</li>
                        <li>Plate: 1</li>
                        <li>Spoon: 1</li>
                        <li>Tea/coffee mug (plastic): 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-plus-circle"></i> Miscellaneous</h4>
                      <ul>
                        <li className="optional">Camera: (Optional)</li>
                        <li>Personal medicines: Bring plenty if needed; consult your doctor before the trek.</li>
                        <li className="optional">Dry fruits, nuts, chocolate bars (Optional)</li>
                      </ul>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>All meals during the trek (breakfast, lunch, dinner)</li>
                    <li>Accommodation in tents during the trek</li>
                    <li>Professional trek leader and support staff</li>
                    <li>All necessary permits and entry fees</li>
                    <li>First aid kit and emergency support</li>
                    <li>Transportation from Kasol to trek start point and back</li>
                    <li>Camping equipment (tents, sleeping bags, mattresses)</li>
                    <li>Kitchen and dining tent with all necessary utensils</li>
                    <li>Porters for carrying common equipment</li>
                    <li>Safety equipment and communication devices</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>Anything that has not been covered in the inclusions mentioned above</li>
                    <li>Porters or mules to carry your luggage. The cost per bag (10/12 kg) is 300 per day</li>
                    <li>Any kind of personal expenses</li>
                    <li>Any kind of personal medication</li>
                    <li>Transportation to and from Kasol</li>
                    <li>Personal trekking gear and equipment</li>
                    <li>Any additional expenses due to weather conditions or unforeseen circumstances</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div className="booking-card" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>13,800 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Trek Distance:</strong>
                      <span>48 km (circular)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Moderate</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>May to October</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person (Ex. Kasol)</h4>
                  <ul>
                    <li><strong>Quad Sharing:</strong> ₹ 4,999 + 5% GST</li>
                    <li><strong>Triple Sharing:</strong> ₹ 5,499 + 5% GST</li>
                    <li><strong>Double Sharing:</strong> ₹ 5,999 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Kasol:</strong> Kasol Bus Stand between 08:00 AM to 09:00 AM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Sar Pass at 13,800 ft</li>
                    <li>Alpine lakes and meadows</li>
                    <li>Panoramic mountain views</li>
                    <li>Dense pine forests</li>
                    <li>Traditional villages</li>
                    <li>Snow-covered landscapes</li>
                    <li>Exhilarating snow slide</li>
                    <li>Professional trek leader and support staff</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default SarPass;
